import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect
} from 'react'
import { resources } from 'language/resources'
import { enums } from 'language/enums'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'

const baseLocale = resources.sv

type BaseLocale = typeof baseLocale

export type Lang = keyof typeof resources
export type Namespaces = keyof BaseLocale
export type Strings<N extends Namespaces> = keyof BaseLocale[N]
export type TFunction = <N extends Namespaces>(
  namespace: N,
  key: Strings<N>,
  lower?: boolean,
  params?: Record<string, string>
) => string

type EnumNamespaces = keyof (typeof enums)['sv']
type EnumKey<N extends EnumNamespaces> = keyof (typeof enums)['sv'][N]
type EnumValue<
  N extends EnumNamespaces,
  K extends EnumKey<N>
> = (typeof enums)['sv'][N][K]

export type TranslateEnumFunction = <
  N extends EnumNamespaces,
  K extends EnumKey<N>
>(
  namespace: N,
  key: K
) => (typeof enums)['sv'][N][K]

const LanguageContext = createContext<{
  lang: Lang
  setLang: (lang: Lang) => void
    }>({ lang: 'sv', setLang: () => {} })

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const { account } = useAccountSettings()
  const [lang, setLang] = useState<Lang>(
    account ? account.language || 'sv' : 'sv'
  )

  useEffect(() => {
    if (account?.language) {
      setLang(account.language)
    } else {
      setLang('sv')
    }
  }, [account?.language])

  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  return (
    <LanguageContext.Provider value={{ lang, setLang }}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = () => useContext(LanguageContext)

export const useTranslation = () => {
  const { lang } = useLanguage()

  /**
   * Translates a key from the specified namespace using the current language.
   *
   * Supports:
   * - optional string interpolation using `params` (e.g. `{{name}}`)
   * - optional lowercase formatting
   * - fallback to a global label if key or namespace is missing
   *
   * @template N - The translation namespace (e.g. 'global', 'dashboards')
   * @param {N} namespace - Namespace to fetch the translation from
   * @param {Strings<N> | undefined} key - The key within the namespace
   * @param {boolean} [lower] - Whether to lowercase the result
   * @param {Record<string, string>} [params] - Values to interpolate into
   *   placeholders in the translation string (e.g. `{{name}}`)
   * @returns {string} The translated string or a fallback label
   *
   * @example
   * t('global', 'welcome')
   * // → "Välkommen"
   *
   * @example
   * t('global', 'greeting_with_name', false, { name: 'Kevin' })
   * // → "Hej Kevin"
   */

  function t<N extends Namespaces>(
    namespace: N,
    key: Strings<N> | undefined,
    lower?: boolean,
    params?: Record<string, string>
  ): string {
    if (!key) {
      return resources[lang]['global']['fallback_label']
    }

    const typedNamespace = resources[lang][namespace] as BaseLocale[N]

    if (typedNamespace && key in typedNamespace) {
      let text = typedNamespace[key] as string

      if (params) {
        for (const [k, v] of Object.entries(params)) {
          text = text.replace(new RegExp(`{{${k}}}`, 'g'), v)
        }
      }

      if (lower) {
        return text.toLowerCase()
      }

      return text
    }

    return resources[lang]['global']['fallback_label']
  }

  /**
   * Translates enum values from a dedicated enum namespace.
   *
   * Example:
   * translateEnum('period_filters', PeriodFilter.TODAY)
   */
  function translateEnum<N extends EnumNamespaces, K extends EnumKey<N>>(
    namespace: N,
    key: K
  ): EnumValue<N, K> {
    const value = enums[lang][namespace][key]

    if (!value) {
      return resources[lang]['global']['fallback_label'] as EnumValue<N, K>
    }

    return value
  }

  return { t, translateEnum, lang }
}
